import React, { useEffect, useMemo, useState } from 'react';
import { FiDownload } from 'react-icons/fi';
import { usePlayerDashboard } from './usePlayerDashboard';
import LoadingSkeleton from './LoadingSkeleton';
import SummaryCards from './SummaryCards';
import ActionBar from './ActionBar';
import FiltersPanel from './FiltersPanel';
import ModeSection from './ModeSection';
import ModeTabs from './ModeTabs';
import CompareModesView from './CompareModesView';
import PlayerProfileModal from './PlayerProfileModal/index';
import PlayerComparisonModal from './PlayerComparisonModal';
import EmptyState from './EmptyState';
import '../../styles/PlayerDashboard.css';
import '../../styles/MyStatistics.css';

const PlayerDashboard = ({ requestedPlayer, onPlayerRequestHandled }) => {
  const {
    // State
    teamId,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    matchType,
    setMatchType,
    region,
    setRegion,
    matchTypes,
    availableRegions,
    mapFilters,
    setMapFilters,
    data,
    modeInsights,
    rankingsByMode,
    playerStats,
    statsMaintenance,
    loading,
    selectedPlayer,
    setSelectedPlayer,
    summaryStats,
    playerSearch,
    setPlayerSearch,
    sortConfig,
    comparePlayers,
    showComparison,
    setShowComparison,
    playerImages,
    selectedSeason,
    seasonDocId,
    seasonDisplayLabel,
    setSelectedSeason,
    // Actions
    togglePlayerComparison,
    handleSort,
  } = usePlayerDashboard();
  const [activeMode, setActiveMode] = useState('hardpoint');

  useEffect(() => {
    if (requestedPlayer?.season && String(requestedPlayer.season) !== String(selectedSeason)) {
      setSelectedSeason(requestedPlayer.season);
    }
  }, [requestedPlayer, selectedSeason, setSelectedSeason]);

  useEffect(() => {
    if (!requestedPlayer || loading || (requestedPlayer.season && String(requestedPlayer.season) !== String(selectedSeason))) return;
    const exactRow = requestedPlayer.playerId
      ? playerStats.find((row) => String(row.playerId || row.PlayerId || row.playerID || '') === String(requestedPlayer.playerId))
      : null;
    const name = exactRow?.Player || playerStats.find((row) => row.Player === requestedPlayer.name)?.Player;
    if (!name) return;
    setSelectedPlayer(name);
    onPlayerRequestHandled?.();
  }, [requestedPlayer, loading, selectedSeason, playerStats, setSelectedPlayer, onPlayerRequestHandled]);

  const handlePlayerClick = (playerName) => {
    setSelectedPlayer(playerName);
  };

  const handleMapFilterChange = (mode, value) => {
    setMapFilters(prev => ({ ...prev, [mode]: value }));
  };

  const compareRows = useMemo(() => {
    const grouped = {};
    const normalizeText = (value) => String(value || '').trim().toUpperCase();
    const normalizeResult = (value) => {
      const raw = String(value || '').trim().toUpperCase();
      if (raw === 'WIN' || raw === 'W') return 'Win';
      if (raw === 'LOSS' || raw === 'LOSE' || raw === 'L') return 'Loss';
      return '';
    };
    const withinDate = (row) => {
      if (startDate && row?.Date && row.Date < startDate) return false;
      if (endDate && row?.Date && row.Date > endDate) return false;
      return true;
    };

    (playerStats || []).forEach((row) => {
      const mode = String(row?.Mode || '').toUpperCase();
      if (!['HP', 'SND', 'CTL'].includes(mode)) return;
      if (matchType && normalizeText(row?.MatchType) !== normalizeText(matchType)) return;
      if (region && normalizeText(row?.Region) !== normalizeText(region)) return;
      if (mapFilters[mode] && row?.Map !== mapFilters[mode]) return;
      if (!withinDate(row)) return;

      const player = row?.Player || 'Unknown';
      if (!grouped[player]) {
        grouped[player] = {
          Player: player,
          HP: { k: 0, d: 0 },
          SND: { k: 0, d: 0 },
          CTL: { k: 0, d: 0 },
          score: 0,
          damage: 0,
          count: 0,
          wins: 0,
        };
      }

      grouped[player][mode].k += Number(row?.K || 0);
      grouped[player][mode].d += Number(row?.D || 0);
      grouped[player].score += Number(row?.Score || 0);
      grouped[player].damage += Number(row?.TotalDamage || row?.Damage || 0);
      grouped[player].count += 1;
      if (normalizeResult(row?.Result || row?.WinLose) === 'Win') grouped[player].wins += 1;
    });

    const kd = (m) => (m.d > 0 ? m.k / m.d : m.k);
    return Object.values(grouped)
      .map((p) => ({
        Player: p.Player,
        hpKd: kd(p.HP).toFixed(2),
        sndKd: kd(p.SND).toFixed(2),
        ctlKd: kd(p.CTL).toFixed(2),
        avgDamage: String(Math.round(p.count ? p.damage / p.count : 0)),
        avgScore: String(Math.round(p.count ? p.score / p.count : 0)),
        winPct: `${Math.round(p.count ? ((p.wins / p.count) * 100) : 0)}%`,
      }))
      .sort((a, b) => Number(b.avgScore) - Number(a.avgScore));
  }, [playerStats, startDate, endDate, matchType, region, mapFilters]);

  const modalPlayerNames = useMemo(() => {
    const uniquePlayers = new Set();
    const normalizeText = (value) => String(value || '').trim().toUpperCase();

    (playerStats || []).forEach((row) => {
      if (startDate && row?.Date && row.Date < startDate) return;
      if (endDate && row?.Date && row.Date > endDate) return;
      if (matchType && normalizeText(row?.MatchType) !== normalizeText(matchType)) return;
      if (region && normalizeText(row?.Region) !== normalizeText(region)) return;

      const playerName = String(row?.Player || '').trim();
      if (playerName) {
        uniquePlayers.add(playerName);
      }
    });

    return Array.from(uniquePlayers).sort((a, b) => a.localeCompare(b));
  }, [playerStats, startDate, endDate, matchType, region]);

  const selectedPlayerIndex = useMemo(
    () => modalPlayerNames.indexOf(selectedPlayer),
    [modalPlayerNames, selectedPlayer]
  );

  const previousPlayerName = selectedPlayerIndex > 0
    ? modalPlayerNames[selectedPlayerIndex - 1]
    : null;

  const nextPlayerName = selectedPlayerIndex >= 0 && selectedPlayerIndex < modalPlayerNames.length - 1
    ? modalPlayerNames[selectedPlayerIndex + 1]
    : null;

  const exportActiveTabCSV = () => {
    const modeByTab = { hardpoint: 'HP', snd: 'SND', control: 'CTL' };
    const escape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    let headers = [];
    let rows = [];
    let fileTag = activeMode;

    if (activeMode === 'compare') {
      headers = ['Player', 'HP K/D', 'S&D K/D', 'CTRL K/D', 'Avg Damage', 'Avg Score', 'Win %'];
      rows = compareRows.map((r) => [r.Player, r.hpKd, r.sndKd, r.ctlKd, r.avgDamage, r.avgScore, r.winPct]);
      fileTag = 'compare_modes';
    } else {
      const mode = modeByTab[activeMode];
      const source = data?.[mode] || [];
      headers = ['Player', 'Avg K/D', 'Avg Score', 'Kills', 'Deaths', 'Assists', 'Impact', 'Win %'];
      rows = source.map((row) => {
        const kd = row.D > 0 ? (row.K / row.D) : row.K;
        return [
          row.Player,
          kd.toFixed(2),
          row.Score || 0,
          row.K || 0,
          row.D || 0,
          row.A || 0,
          row.Impact || 0,
          `${Math.round(Number(row.winRate || 0))}%`,
        ];
      });
      fileTag = mode?.toLowerCase() || 'mode';
    }

    const csv = [headers.map(escape).join(','), ...rows.map((r) => r.map(escape).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `player_dashboard_${fileTag}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const modeConfigByTab = {
    hardpoint: { code: 'HP', label: 'Hardpoint' },
    snd: { code: 'SND', label: 'Search & Destroy' },
    control: { code: 'CTL', label: 'Control' },
  };

  const activeModeConfig = modeConfigByTab[activeMode];

  if (loading && playerStats.length === 0) {
    return (
      <div className="player-dashboard">
        <h1>Player Dashboard</h1>
        <LoadingSkeleton />
      </div>
    );
  }

  if (statsMaintenance && playerStats.length === 0) {
    return (
      <div className="player-dashboard">
        <div className="dashboard-header">
          <h1>Player Dashboard</h1>
        </div>
        <div className="mode-section">
          <div className="mode-section-block dashboard-surface">
            <div className="mode-section-content">
              <EmptyState title="No Statistics Available" />
              <p style={{ marginTop: '12px', color: '#888', textAlign: 'center' }}>
                Statistics are temporarily under maintenance while we rebuild this data source for better performance.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="player-dashboard">
      <div className="dashboard-header">
        <h1>Player Dashboard</h1>
        <button className="export-btn" onClick={exportActiveTabCSV}>
          <FiDownload /> Export CSV
        </button>
      </div>

      <SummaryCards summaryStats={summaryStats} />

      <ActionBar
        playerSearch={playerSearch}
        onClearSearch={() => setPlayerSearch('')}
        comparePlayers={comparePlayers}
        onRemoveFromComparison={togglePlayerComparison}
        onViewComparison={() => setShowComparison(true)}
      />

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', gap: '24px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', width: '100%' }}>
          <FiltersPanel
            startDate={startDate}
            onStartDateChange={setStartDate}
            endDate={endDate}
            onEndDateChange={setEndDate}
            matchType={matchType}
            onMatchTypeChange={(e) => setMatchType(e.target.value)}
            matchTypes={matchTypes}
            region={region}
            onRegionChange={(e) => setRegion(e.target.value)}
            availableRegions={availableRegions}
            selectedSeason={selectedSeason}
            onSeasonChange={setSelectedSeason}
          />
        </div>
      </div>

      <ModeTabs activeMode={activeMode} onChange={setActiveMode} />

      {activeMode === 'compare' ? (
        <div className="mode-section">
          <div className="mode-section-block dashboard-surface">
            <div className="mode-section-content">
              <CompareModesView rows={compareRows} onPlayerClick={handlePlayerClick} playerImages={playerImages} />
            </div>
          </div>
        </div>
      ) : (
        <div className="mode-section">
          <ModeSection
            mode={activeModeConfig.code}
            data={data[activeModeConfig.code]}
            insights={modeInsights[activeModeConfig.code]}
            rankings={rankingsByMode[activeModeConfig.code]}
            mapFilter={mapFilters[activeModeConfig.code]}
            onMapFilterChange={handleMapFilterChange}
            loading={loading}
            sortConfig={sortConfig}
            onSort={handleSort}
            playerSearch={playerSearch}
            comparePlayers={comparePlayers}
            onPlayerClick={handlePlayerClick}
            onToggleComparison={togglePlayerComparison}
            playerImages={playerImages}
          />
        </div>
      )}

      {selectedPlayer && (
        <PlayerProfileModal
          playerId={playerStats.find((row) => row.Player === selectedPlayer)?.playerId || undefined}
          playerName={selectedPlayer}
          onClose={() => setSelectedPlayer(null)}
          onPreviousPlayer={previousPlayerName ? () => setSelectedPlayer(previousPlayerName) : null}
          onNextPlayer={nextPlayerName ? () => setSelectedPlayer(nextPlayerName) : null}
          playerNames={modalPlayerNames}
          playerStats={playerStats}
          teamId={teamId}
          selectedSeason={selectedSeason}
          seasonDocId={seasonDocId}
          seasonDisplayLabel={seasonDisplayLabel}
          startDate={startDate}
          endDate={endDate}
          matchType={matchType}
          region={region}
          playerImage={playerImages[selectedPlayer]}
          playerImages={playerImages}
        />
      )}

      {showComparison && comparePlayers.length > 0 && (
        <PlayerComparisonModal
          players={comparePlayers}
          onClose={() => setShowComparison(false)}
          playerStats={playerStats}
          startDate={startDate}
          endDate={endDate}
          matchType={matchType}
          region={region}
          playerImages={playerImages}
        />
      )}
    </div>
  );
};

export default PlayerDashboard;

